import { Box, Grid, Typography, debounce, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation } from 'react-router-dom';
import ActivityLibraryComponent from '../ActivityLibrary';
import { useAppSelector } from '../../../redux/hooks';
import {
  IFolderType,
  clearActivitiesInsideFolder,
  clearFolder,
  getActivitiesInsideFolder,
  resetRestoreActivitySuccess,
  setActivityLibraryBlock,
} from '../../../redux/activityLibrary/activityLibrarySlice';
import ActivityLibraryTopHeader from '../ActivityLibraryTopHeader';
import {
  ActivityLibraryFoldersLoadingView,
  SingleFolderSearchBarLoadingView,
} from '../ActivityLibraryLoadingView';
import { ACTIVITY_LIBRARY_ROUTE } from '../../../utils/routes';
import NoResultsFound from '../NoResultsFound';

const SingleFolder = () => {
  const [searchText, setSearchText] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const { totalActivityCount, activity } = useAppSelector(
    (state) => state.activityLibrary.activitiesInsideFolder
  );
  const { id, folderType, name } = useAppSelector((state) => state.activityLibrary.folder);
  const loading = useAppSelector((state) => state.activityLibrary.getActivitiesInsideFolderLoading);
  const {
    restoreActivityLoading,
    restoreActivitySuccess,
    permenantlyDeleteActivitySuccess,
    isDeleteActivitySuccess,
  } = useAppSelector((state) => state.activityLibrary);

  useEffect(() => {
    if (!coachId || !id) return;
    dispatch(getActivitiesInsideFolder({ coachId, folderId: id, page: 1, pageSize: 24 }));
  }, [coachId, id]);

  const debouncedFetchUserFoldersAndActivities = useCallback(
    debounce(({ filterValue }: { filterValue: string }) => {
      if (!coachId) return;
      dispatch(
        getActivitiesInsideFolder({
          coachId,
          filterValue,
          folderId: id,
          page: 1,
          pageSize: 24,
          isSearch: true,
        })
      );
    }, 1000),
    [coachId, searchText, dispatch]
  );

  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    debouncedFetchUserFoldersAndActivities({ filterValue: event.target.value });
  };

  const fetchMoreData = () => {
    if (activity.length >= totalActivityCount) {
      setHasMore(false);
      return;
    }
    const newPage = page + 1;
    setPage(newPage);
    if (!coachId || !id) return;
    dispatch(
      getActivitiesInsideFolder({
        coachId,
        filterValue: searchText,
        folderId: id,
        page: newPage,
        pageSize: 24,
      })
    );
  };

  useEffect(
    () => () => {
      dispatch(clearActivitiesInsideFolder());
    },
    []
  );

  useEffect(() => {
    if (
      (restoreActivitySuccess || permenantlyDeleteActivitySuccess || isDeleteActivitySuccess) &&
      coachId
    ) {
      dispatch(clearActivitiesInsideFolder());
      dispatch(
        getActivitiesInsideFolder({
          coachId,
          folderId: id,
          page,
          pageSize: 24,
        })
      );
    }
    if (restoreActivitySuccess) {
      dispatch(resetRestoreActivitySuccess());
    }
  }, [restoreActivitySuccess, permenantlyDeleteActivitySuccess, isDeleteActivitySuccess]);

  const handleBack = () => {
    dispatch(setActivityLibraryBlock());
    dispatch(clearFolder());
  };

  if (location.pathname === ACTIVITY_LIBRARY_ROUTE) {
    return (
      <Box height="100%" width="100%" pr="10px" alignContent="flex-start">
        {loading || restoreActivityLoading ? (
          <Grid gap={4} display="flex" flexDirection="column">
            <SingleFolderSearchBarLoadingView />
            <ActivityLibraryFoldersLoadingView />
          </Grid>
        ) : (
          <Grid gap={4} display="flex" flexDirection="column" height="100%">
            <ActivityLibraryTopHeader
              searchHandler={searchHandler}
              searchText={searchText}
              isSingleFolder
              isTrash={folderType === IFolderType.TRASH}
            />
            {activity.length === 0 ? (
              <NoResultsFound />
            ) : (
              <InfiniteScroll
                dataLength={activity.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={null}
              >
                <ActivityLibraryComponent activityLibraries={activity} />
              </InfiniteScroll>
            )}
          </Grid>
        )}
      </Box>
    );
  }
  return (
    <Box height="100%" borderLeft={theme.borderStyles.commonBorders} width="100%">
      {loading || restoreActivityLoading ? (
        <Grid gap={4} display="flex" flexDirection="column" p={4}>
          <SingleFolderSearchBarLoadingView />
          <ActivityLibraryFoldersLoadingView />
        </Grid>
      ) : (
        <Grid
          gap={4}
          display="flex"
          flexDirection="column"
          bgcolor="rgba(255, 221, 200, 0.25)"
          p={4}
          minHeight="100%"
        >
          <Grid
            display="flex"
            flexDirection="row"
            gap={2}
            alignItems="center"
            sx={{ cursor: 'pointer' }}
            onClick={handleBack}
          >
            <img
              src="/assets/icons/activity-library-arrow.svg"
              alt="chevron-right"
              style={{
                width: '8px',
                height: '19px',
              }}
            />
            <Typography variant="h6" color="primary">
              {name}
            </Typography>
          </Grid>
          <InfiniteScroll
            dataLength={activity.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={null}
          >
            <ActivityLibraryComponent activityLibraries={activity} />
          </InfiniteScroll>
        </Grid>
      )}
    </Box>
  );
};

export default SingleFolder;
